import React, { useState } from 'react';
import { Info, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, Alert, Badge } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const PDFPageCounter = () => {
    const [file, setFile] = useState(null);
    const [info, setInfo] = useState(null);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);

                const pdfInfo = {
                    fileName: selectedFile.name,
                    fileSize: (selectedFile.size / 1024).toFixed(2) + ' KB',
                    pages: pdf.getPageCount(),
                    title: pdf.getTitle() || 'N/A',
                    author: pdf.getAuthor() || 'N/A',
                    subject: pdf.getSubject() || 'N/A',
                    keywords: pdf.getKeywords() || 'N/A',
                    creator: pdf.getCreator() || 'N/A',
                    producer: pdf.getProducer() || 'N/A',
                    creationDate: pdf.getCreationDate()?.toString() || 'N/A',
                    modificationDate: pdf.getModificationDate()?.toString() || 'N/A'
                };

                setInfo(pdfInfo);
                toast.success('PDF information extracted!');
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    return (
        <div>
            <div className="tool-header">
                <div className="tool-icon-wrapper">
                    <Info size={40} color="var(--color-text-primary)" />
                </div>
                <div className="tool-header-content">
                    <h1 className="tool-title">PDF Page Counter</h1>
                    <p className="tool-description">
                        View detailed information about your PDF: page count, file size, metadata, and more.
                    </p>
                </div>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <>
                    <Alert variant="success" title="PDF Loaded">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>{file.name}</span>
                            <Button variant="ghost" size="sm" onClick={() => { setFile(null); setInfo(null); }}>
                                Change PDF
                            </Button>
                        </div>
                    </Alert>

                    {info && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Card>
                                <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                    PDF Information
                                </h3>

                                <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
                                    {[
                                        { label: 'File Name', value: info.fileName },
                                        { label: 'File Size', value: info.fileSize },
                                        { label: 'Total Pages', value: info.pages, highlight: true },
                                        { label: 'Title', value: info.title },
                                        { label: 'Author', value: info.author },
                                        { label: 'Subject', value: info.subject },
                                        { label: 'Keywords', value: info.keywords },
                                        { label: 'Creator', value: info.creator },
                                        { label: 'Producer', value: info.producer },
                                        { label: 'Creation Date', value: info.creationDate },
                                        { label: 'Modification Date', value: info.modificationDate }
                                    ].map((item, i) => (
                                        <div key={i} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            padding: 'var(--spacing-3)',
                                            backgroundColor: item.highlight ? 'var(--color-accent-subtle)' : 'var(--color-surface-elevated)',
                                            borderRadius: 'var(--radius-md)',
                                            borderLeft: item.highlight ? '4px solid var(--color-accent)' : 'none'
                                        }}>
                                            <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text-secondary)' }}>
                                                {item.label}:
                                            </span>
                                            <span style={{ fontWeight: item.highlight ? 'var(--font-bold)' : 'var(--font-medium)', color: item.highlight ? 'var(--color-accent)' : 'var(--color-text-primary)' }}>
                                                {item.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PDFPageCounter;
